import React, { useState, useEffect, useMemo } from 'react';
import { cashBankApi, sitesApi } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Banknote, Landmark, Smartphone, TrendingUp, TrendingDown, Wallet } from 'lucide-react';

const MODES = [
  { key: 'cash', label: 'Cash', icon: Banknote },
  { key: 'bank', label: 'Bank', icon: Landmark },
];

export default function CashBankPage() {
  const [flow, setFlow] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sitesApi.list().then(r => setSites(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    cashBankApi.get(siteId || undefined)
      .then(r => setFlow(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading || !flow) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const summaryTotals = (() => {
    const totalIn = Object.values(flow).reduce((s, m) => s + (m.in || 0), 0);
    const totalOut = Object.values(flow).reduce((s, m) => s + (m.out || 0), 0);
    const netBalance = Object.values(flow).reduce((s, m) => s + (m.balance || 0), 0);
    return { totalIn, totalOut, netBalance };
  })();

  return (
    <div className="space-y-6" data-testid="cash-bank-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Cash & Bank Flow</h1>
          <p className="text-sm text-muted-foreground mt-1">Track money flow by payment mode</p>
        </div>
        <Select value={siteId} onValueChange={v => setSiteId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[200px]" data-testid="cash-bank-site-filter">
            <SelectValue placeholder="All Sites" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sites</SelectItem>
            {sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {MODES.map(mode => {
          const data = flow[mode.key] || { in: 0, out: 0, balance: 0 };
          return (
            <div key={mode.key} className="bg-card border border-border p-6 space-y-4" data-testid={`flow-card-${mode.key}`}>
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-muted rounded">
                  <mode.icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold tracking-tight">{mode.label}</h3>
              </div>
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border">
                <div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground uppercase tracking-wider">
                    <TrendingUp className="w-3 h-3 text-emerald-600" /> In
                  </div>
                  <div className="font-mono text-sm font-medium text-emerald-700 dark:text-emerald-400 mt-1">
                    {'\u20B9'}{data.in.toLocaleString('en-IN')}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground uppercase tracking-wider">
                    <TrendingDown className="w-3 h-3 text-red-600" /> Out
                  </div>
                  <div className="font-mono text-sm font-medium text-red-700 dark:text-red-400 mt-1">
                    {'\u20B9'}{data.out.toLocaleString('en-IN')}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground uppercase tracking-wider">
                    <Wallet className="w-3 h-3" /> Balance
                  </div>
                  <div className={`font-mono text-sm font-medium mt-1 ${data.balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                    {'\u20B9'}{data.balance.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="bg-card border border-border p-6" data-testid="cash-bank-summary">
        <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-4">Overall Summary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-emerald-50 dark:bg-emerald-950/30 p-3 sm:p-4 border border-emerald-200 dark:border-emerald-900">
            <div className="text-[10px] sm:text-xs text-emerald-700 dark:text-emerald-400 uppercase tracking-wider font-semibold">Total In</div>
            <div className="font-mono text-base sm:text-lg font-bold text-emerald-700 dark:text-emerald-400 mt-1 break-all">
              {'\u20B9'}{summaryTotals.totalIn.toLocaleString('en-IN')}
            </div>
          </div>
          <div className="bg-red-50 dark:bg-red-950/30 p-3 sm:p-4 border border-red-200 dark:border-red-900">
            <div className="text-[10px] sm:text-xs text-red-700 dark:text-red-400 uppercase tracking-wider font-semibold">Total Out</div>
            <div className="font-mono text-base sm:text-lg font-bold text-red-700 dark:text-red-400 mt-1 break-all">
              {'\u20B9'}{summaryTotals.totalOut.toLocaleString('en-IN')}
            </div>
          </div>
          <div className="bg-muted p-3 sm:p-4">
            <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider font-semibold">Net Balance</div>
            <div className={`font-mono text-base sm:text-lg font-bold mt-1 break-all ${summaryTotals.netBalance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
              {'\u20B9'}{summaryTotals.netBalance.toLocaleString('en-IN')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
